import { z } from 'zod';
import type { Currency } from '../money/money';
import { moneySchema } from './common.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';
import { RATE_BASES, type RateBasis } from './rate-card.schemas';

/**
 * Off-card pricing (PRD → Rate cards → Off-card jobs; gitaumoses4/chukua#35): a job the rate card could
 * not price is `pricingRequired` until a clerk's typed rate is approved by someone else.
 */

export const MANUAL_PRICE_REQUEST_STATUSES = ['pending_approval', 'approved', 'rejected'] as const;
export type ManualPriceRequestStatus = (typeof MANUAL_PRICE_REQUEST_STATUSES)[number];

/** `POST /jobs/:id/manual-price` — a price of zero is not a price, and the reason is what the approver reads. */
export const manualPriceInputSchema = z.object({
  amount: moneySchema.refine((money) => money.amountMinor > 0, { message: 'Amount must be greater than zero', path: ['amountMinor'] }),
  basis: z.enum(RATE_BASES),
  reason: z.string().trim().min(1).max(500),
});
export type ManualPriceInput = z.infer<typeof manualPriceInputSchema>;

/** `POST /jobs/:id/manual-price/{approve,reject}` — a reject must say why, since the clerk revises from it. */
export const manualPriceApprovalSchema = z.object({ reason: z.string().trim().max(500).optional() });
export type ManualPriceApprovalInput = z.infer<typeof manualPriceApprovalSchema>;

export const manualPriceRejectionSchema = z.object({ reason: z.string().trim().min(1).max(500) });
export type ManualPriceRejectionInput = z.infer<typeof manualPriceRejectionSchema>;

export interface ManualPriceRequestView {
  id: string;
  jobId: string;
  amount: { amountMinor: number; currency: Currency };
  basis: RateBasis;
  reason: string;
  status: ManualPriceRequestStatus;
  submittedById: string;
  submittedAt: string;
  decidedById: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
}

/** `GET /offcard-pricing-report` — one row per customer, lane, job type, rate basis, month and currency; `q` searches customer name, zone codes and job type. */
export const offcardReportQuerySchema = defineListQuery({
  sortFields: ['period', 'offCardCount', 'onCardCount', 'originZoneCode', 'destinationZoneCode', 'jobTypeCode'],
  defaultSort: { sort: 'period', order: 'desc' },
  filters: {
    customerId: listFilter(z.string().uuid()),
    originZoneCode: listFilter(z.string().trim().min(1).max(32)),
    destinationZoneCode: listFilter(z.string().trim().min(1).max(32)),
    periodFrom: z.iso.date().optional(),
    periodTo: z.iso.date().optional(),
  },
});
export type OffcardReportQuery = z.infer<typeof offcardReportQuerySchema>;

export interface OffcardReportGroup {
  customerId: string;
  customerName: string;
  originZoneCode: string;
  destinationZoneCode: string;
  /** Cargo kind: prices of different job types or rate bases are never compared. */
  jobTypeCode: string;
  rateBasis: RateBasis;
  /** First day of the month, ISO date. */
  period: string;
  currency: Currency;
  onCardCount: number;
  offCardCount: number;
  /** Average off-card price in this month minus the average card price for the same customer, lane, job type, basis and currency across all months; null when the card has no job there or this group has no off-card job. */
  variance: { amountMinor: number; currency: Currency } | null;
  /** Off-card jobs here and no card-priced job on this lane (job type, basis, currency) in any month: the card is missing this lane. */
  cardIncomplete: boolean;
}

/**
 * `POST /jobs/:id/manual-price/propose-line` — offers the approved price to the rate card as a proposal. Both
 * fields override the pre-fill: the date defaults to tomorrow (approval refuses a line that starts today or
 * earlier) and a container line's empty-return flag to "billed separately", since the job does not say.
 */
export const proposeLineFromJobSchema = z.object({
  validFrom: z.iso.date().optional(),
  emptyReturnIncluded: z.boolean().optional(),
});
export type ProposeLineFromJobInput = z.infer<typeof proposeLineFromJobSchema>;
